import { signalRService } from "./signalr.service";
import { debugLog, debugError } from "../common/utils/debug";
import { MicVAD } from "@ricky0123/vad-web";

/**
 * Silero Neural VAD Streaming Service
 * 
 * Uses the Silero VAD neural network (~2MB ONNX model) for accurate speech detection
 * instead of simple energy-based (dB threshold) detection.
 * 
 * Architecture:
 * - MicVAD handles microphone capture and runs Silero model per frame
 * - onFrameProcessed callback receives speech probability (0-1) for each frame
 * - Frames with speech probability > threshold are sent to Azure STT
 * - Still uses Azure Speech SDK for actual transcription (no change to backend)
 * 
 * Benefits over energy-based VAD:
 * - Distinguishes speech from typing, fans, coughs, music
 * - Works at any volume level (no dB threshold tuning)
 * - Handles noisy environments much better
 * - Pre-trained on thousands of hours of speech data
 * 
 * TIMING COORDINATION (CRITICAL):
 * - Backend SPEECHTOTEXT__SILENCETIMEOUTMS = 3000ms (Azure SDK silence detection)
 * - Frontend redemptionMs = 3500ms (keeps isSpeechActive=true during silence)
 * - Frontend trailing silence = 2000ms (extra buffer after onSpeechEnd)
 * - This ensures frontend NEVER stops sending before backend finalizes
 * 
 * Timeline when user stops speaking:
 * 1. User stops speaking at T=0
 * 2. Silero keeps isSpeechActive=true for 3500ms (redemptionMs)
 * 3. Backend receives continuous audio, starts 3000ms silence timer
 * 4. At T=3000ms, backend finalizes (while frontend still sending)
 * 5. At T=3500ms, Silero fires onSpeechEnd, frontend starts trailing silence
 * 6. At T=5500ms, trailing silence ends, frontend stops sending
 * 7. Backend already finalized at T=3000ms - no timeout issues ✅
 */
class SileroVADStreamingService {
  private isRecording: boolean = false;
  private isStreaming: boolean = false;
  private isMuted: boolean = true; // Start muted by default
  private micVAD: MicVAD | null = null;
  private chunkCount: number = 0;
  
  // Neural VAD state
  private isSpeechActive: boolean = false;
  
  // ============================================================================
  // SILERO VAD CONFIGURATION
  // These values are tuned for coordination with backend Azure Speech SDK
  // ============================================================================
  
  // positiveSpeechThreshold: probability to START detecting speech (0-1)
  // Lower = more sensitive to quiet speech like "No", "Yes"
  // Default: 0.5, We use: 0.4 for better sensitivity
  private readonly positiveSpeechThreshold: number = 0.4;
  
  // negativeSpeechThreshold: probability to END detecting speech (0-1)
  // Lower than positive creates hysteresis (prevents rapid on/off)
  // Default: 0.35, We use: 0.25 for more tolerance during pauses
  private readonly negativeSpeechThreshold: number = 0.25;
  
  // redemptionMs: How long to keep isSpeechActive=true after speech probability drops
  // CRITICAL: Must be >= backend SPEECHTOTEXT__SILENCETIMEOUTMS (3000ms) + buffer
  // This ensures we keep sending audio while backend's silence timer is running
  // Default: 1000, We use: 3500 (3000ms backend + 500ms safety buffer)
  private readonly redemptionMs: number = 3500;
  
  // minSpeechMs: Minimum speech duration to trigger onSpeechEnd
  // Prevents very short sounds (clicks, pops) from triggering false positives
  // Default: 250, We use: 150 to catch quick "No", "Yes", "Okay"
  private readonly minSpeechMs: number = 150;
  
  // preSpeechPadMs: Audio to include BEFORE speech start in onSpeechEnd callback
  // Critical for catching the beginning of short utterances
  // Default: 300, We use: 500 for extra safety on word beginnings
  private readonly preSpeechPadMs: number = 500;
  
  // ============================================================================
  // TRAILING SILENCE CONFIGURATION
  // Extra safety buffer after onSpeechEnd to ensure backend finalizes
  // ============================================================================
  
  // After onSpeechEnd fires, continue sending frames for this many more frames
  // At ~62 frames/sec (16ms frames), 125 frames = ~2 seconds
  // This is a safety net in case backend needs more time
  private trailingSilenceFrames: number = 0;
  private readonly maxTrailingSilenceFrames: number = 125; // ~2 seconds
  private isInTrailingSilence: boolean = false;
  
  // ============================================================================
  // WARMUP CONFIGURATION
  // Send first N frames unconditionally to prime Azure STT session
  // ============================================================================
  
  private warmupFrames: number = 0;
  private readonly maxWarmupFrames: number = 100; // ~1.6s of audio
  
  // Model loading state
  private isModelLoading: boolean = false;
  private modelLoadPromise: Promise<void> | null = null;

  // No constructor needed - service maintains continuous STT connection for instant response

  /**
   * Pre-initialize VAD model and audio pipeline without starting streaming
   * This eliminates setup delay when unmuting
   */
  async initialize(): Promise<void> {
    if (this.micVAD) {
      debugLog('🔄 [VAD] Silero VAD already initialized');
      return;
    }

    // Prevent concurrent initialization
    if (this.isModelLoading && this.modelLoadPromise) {
      debugLog('🔄 [VAD] Model already loading, waiting...');
      await this.modelLoadPromise;
      return;
    }

    this.isModelLoading = true;
    this.modelLoadPromise = this.initializeInternal();
    
    try {
      await this.modelLoadPromise;
    } finally {
      this.isModelLoading = false;
      this.modelLoadPromise = null;
    }
  }

  private async initializeInternal(): Promise<void> {
    try {
      debugLog('🧠 [VAD] Initializing Silero Neural VAD model...');
      const startTime = performance.now();
      
      this.micVAD = await MicVAD.new({
        // Silero model configuration
        model: "v5", // Use latest Silero v5 model
        positiveSpeechThreshold: this.positiveSpeechThreshold,
        negativeSpeechThreshold: this.negativeSpeechThreshold,
        redemptionMs: this.redemptionMs,
        minSpeechMs: this.minSpeechMs,
        preSpeechPadMs: this.preSpeechPadMs,
        
        // Asset paths - served from /public folder
        baseAssetPath: "/",
        onnxWASMBasePath: "/",
        
        // Audio configuration
        // MicVAD handles getUserMedia internally with sensible defaults
        
        // Frame-by-frame callback - this is where we send audio to Azure
        onFrameProcessed: (probabilities, frame) => {
          this.handleFrameProcessed(probabilities, frame);
        },
        
        // Speech boundary callbacks for logging
        onSpeechStart: () => {
          debugLog('🗣️ [VAD] Silero detected speech start');
          this.isSpeechActive = true;
        },
        
        onSpeechEnd: (audio) => {
          debugLog('🤐 [VAD] Silero detected speech end, audio samples:', audio.length);
          this.isSpeechActive = false;
          // Start trailing silence period - continue sending frames to ensure backend finalizes
          this.isInTrailingSilence = true;
          this.trailingSilenceFrames = 0;
          debugLog('📤 [VAD] Starting trailing silence (sending for', this.maxTrailingSilenceFrames, 'more frames)');
        },
        
        onVADMisfire: () => {
          debugLog('⚡ [VAD] VAD misfire (speech too short)');
        },
      });
      
      const loadTime = performance.now() - startTime;
      debugLog(`✅ [VAD] Silero Neural VAD initialized in ${loadTime.toFixed(0)}ms`);
      
      // Start paused - will resume when user starts interview
      this.isMuted = true;
      
    } catch (error) {
      debugError('[VAD] ❌ Failed to initialize Silero VAD:', error);
      throw error;
    }
  }
  
  /**
   * Handle each processed audio frame from Silero VAD
   * This runs ~62 times per second (16ms frames at 16kHz)
   * 
   * Decision logic for sending frames:
   * 1. WARMUP: First ~1.6s after unmute - send all frames unconditionally
   * 2. SPEECH ACTIVE: isSpeechActive=true - send (Silero detected speech)
   * 3. TRAILING SILENCE: After onSpeechEnd - send for 2 more seconds
   * 4. PROBABILITY CHECK: If probability > threshold - send (might be speech)
   * 5. Otherwise - don't send (silence/noise)
   */
  private handleFrameProcessed(
    probabilities: { isSpeech: number; notSpeech: number },
    frame: Float32Array
  ): void {
    // Skip if muted or not streaming
    if (this.isMuted || !this.isStreaming) {
      return;
    }
    
    try {
      const isSpeechProbability = probabilities.isSpeech;
      
      // PHASE 1: WARMUP - send all frames to prime Azure STT
      const isWarmup = this.warmupFrames < this.maxWarmupFrames;
      if (isWarmup) {
        this.warmupFrames++;
        
        // Track if speech starts during warmup
        if (isSpeechProbability > this.positiveSpeechThreshold && !this.isSpeechActive) {
          debugLog(`🗣️ [VAD] Speech during warmup (prob: ${isSpeechProbability.toFixed(2)})`);
          this.isSpeechActive = true;
        }
        
        this.sendFrame(frame);
        return;
      }
      
      // PHASE 2: TRAILING SILENCE - continue sending after onSpeechEnd
      if (this.isInTrailingSilence) {
        this.trailingSilenceFrames++;
        
        // Check if trailing silence period is complete
        if (this.trailingSilenceFrames >= this.maxTrailingSilenceFrames) {
          debugLog('✅ [VAD] Trailing silence complete, stopping transmission');
          this.isInTrailingSilence = false;
          this.trailingSilenceFrames = 0;
          // Don't send this frame - we're done
          return;
        }
        
        // If new speech starts during trailing silence, cancel it
        if (isSpeechProbability > this.positiveSpeechThreshold) {
          debugLog('🗣️ [VAD] New speech during trailing silence, resuming');
          this.isInTrailingSilence = false;
          this.trailingSilenceFrames = 0;
          this.isSpeechActive = true;
        }
        
        // Send trailing silence frame
        this.sendFrame(frame);
        return;
      }
      
      // PHASE 3: NORMAL OPERATION - use neural VAD state + probability
      // Send if:
      // - Silero says speech is active (isSpeechActive from onSpeechStart)
      // - OR probability exceeds threshold (catches speech Silero might miss)
      const shouldSend = this.isSpeechActive || isSpeechProbability > this.negativeSpeechThreshold;
      
      if (shouldSend) {
        this.sendFrame(frame);
      }
      
    } catch (error) {
      debugError('[VAD] Error processing frame:', error);
    }
  }
  
  /**
   * Convert Float32Array frame to PCM16 and send to Azure
   */
  private sendFrame(frame: Float32Array): void {
    // Convert Float32Array (-1 to 1) to PCM16 Int16Array
    const pcm16 = new Int16Array(frame.length);
    for (let i = 0; i < frame.length; i++) {
      const s = Math.max(-1, Math.min(1, frame[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    
    this.chunkCount++;
    this.sendAudioChunk(pcm16.buffer as ArrayBuffer);
  }

  /**
   * Start audio streaming to Azure Speech Service
   */
  async start(): Promise<void> {
    if (this.isRecording) {
      debugLog("⏭️ [VAD] Already recording, skipping start");
      return;
    }

    try {
      debugLog("🎙️ [VAD] Starting Silero VAD streaming...");
      
      // Initialize if not already done
      if (!this.micVAD) {
        debugLog("🔧 [VAD] VAD not initialized, initializing now...");
        await this.initialize();
      }
      
      if (!this.micVAD) {
        throw new Error("Failed to initialize MicVAD");
      }
      
      // Start the MicVAD (requests microphone, starts processing)
      this.micVAD.start();
      
      // Unmute to allow audio transmission
      this.isMuted = false;
      this.isRecording = true;
      this.isStreaming = true;
      this.chunkCount = 0;
      this.warmupFrames = 0;
      this.isInTrailingSilence = false;
      this.trailingSilenceFrames = 0;
      
      // Start backend streaming
      debugLog("📡 [VAD] Starting Azure streaming recognition...");
      signalRService.startStreamingRecognition()
        .then(() => debugLog("✅ [VAD] Azure streaming recognition started"))
        .catch(err => debugError('[VAD] ❌ Failed to start backend streaming:', err));
      
      debugLog("✅ [VAD] Silero VAD streaming started");
    } catch (error) {
      debugError('[VAD] ❌ Failed to start:', error);
      throw error;
    }
  }

  /**
   * Stop audio streaming
   */
  stop(): void {
    if (!this.isRecording) {
      return;
    }

    debugLog('🛑 [VAD] Stopping Silero VAD...');
    this.isRecording = false;
    this.isSpeechActive = false;
    this.isInTrailingSilence = false;
    this.trailingSilenceFrames = 0;

    // Pause MicVAD (stops microphone capture)
    if (this.micVAD) {
      this.micVAD.pause();
    }

    if (this.isStreaming) {
      this.stopStreaming();
    }
  }

  /**
   * Check if currently recording
   */
  isActive(): boolean {
    return this.isRecording;
  }

  /**
   * Set mute state - when muted, audio frames are not sent to backend
   */
  setMuted(muted: boolean): void {
    const wasMuted = this.isMuted;
    this.isMuted = muted;
    
    if (muted && !wasMuted) {
      debugLog('🔇 [VAD] Muted - pausing audio transmission');
      // Keep MicVAD running but flag prevents sending frames
      // This keeps the model warm for instant unmute
    } else if (!muted && wasMuted) {
      debugLog('🔊 [VAD] Unmuted - resuming audio transmission');
      // Reset warmup counter to prime Azure STT
      this.warmupFrames = 0;
      this.isSpeechActive = false;
      // Clear any trailing silence state from previous speech
      this.isInTrailingSilence = false;
      this.trailingSilenceFrames = 0;
      
      // Ensure streaming is active
      if (!this.isStreaming && this.isRecording) {
        debugLog('🚀 [VAD] Restarting Azure streaming session...');
        signalRService.startStreamingRecognition()
          .then(() => {
            this.isStreaming = true;
            debugLog("✅ [VAD] Azure streaming session restarted");
          })
          .catch(err => debugError('[VAD] ❌ Failed to restart streaming:', err));
      }
    }
  }

  /**
   * Check if user is currently speaking (speech is active)
   */
  isUserSpeaking(): boolean {
    return this.isSpeechActive;
  }

  /**
   * Clean up all resources
   */
  async destroy(): Promise<void> {
    debugLog('🧹 [VAD] Destroying Silero VAD service...');
    
    if (this.isStreaming) {
      await this.stopStreaming();
    }

    if (this.micVAD) {
      this.micVAD.pause();
      // MicVAD doesn't have a destroy method, pause is sufficient
      this.micVAD = null;
    }

    this.isRecording = false;
    this.isStreaming = false;
    this.isMuted = true;
    
    debugLog('✅ [VAD] Silero VAD service destroyed');
  }

  /**
   * Send audio chunk to Azure
   */
  private sendAudioChunk(audioData: ArrayBuffer): void {
    signalRService.sendAudioChunk(audioData).catch(() => {
      // Silently handle errors to avoid flooding console
      // Connection errors are expected during disconnect
    });
  }

  /**
   * Stop streaming to Azure
   */
  private async stopStreaming(): Promise<void> {
    if (!this.isStreaming) {
      return;
    }

    try {
      await signalRService.stopStreamingRecognition();
      this.isStreaming = false;
    } catch (error) {
      this.isStreaming = false;
    }
  }
}

export const sileroVADStreamingService = new SileroVADStreamingService();
export default sileroVADStreamingService;
